import LabelWithPopover from "@/components/form/labels/LabelWithPopover";
import { List } from "@/components/list/List";
import { datePickerFormItemProps, datePickerStandardProps } from "@/utils/componentUtils";
import { formatLocaleCurrency } from "@/utils/formatUtils";
import { resolveFormValidationError, setErrorsToForm } from "@/utils/formUtils";
import { cssVariables, isDefined } from "@/utils/utils";
import { validations } from "@/utils/validationUtils";
import { Alert, Button, Checkbox, Col, DatePicker, Descriptions, Form, Modal, Row, Space, Spin } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { generatePath, Link } from "react-router";
import {
  useCalculateCommissionsBatchPayments,
  useGenerateCommissionsBatchPaymentOrder,
} from "../../../../../common/api/queries/commissions/commissionsBatch";
import { useGetAgentEnumerations } from "../../../../../common/api/queries/enumerations";
import LabelWithTooltip from "../../../../../common/components/form/labels/LabelWithTooltip";
import { ModalSizes, rowGutter } from "../../../../../common/constants";
import { AGENT_ROUTE_PATHS } from "../../../../agent/paths";
import { formatAgentAggregatedName } from "../../../../agent/utils";
import { AgentSelect } from "../../../../enumerations/components/form/AgentSelect";
import { BatchPaymentType, CommissionsBatchStep } from "../../enums";
import { CommissionsBatch, GenerateBatchPaymentOrder } from "../../types";

interface Props {
  batch: CommissionsBatch;
  open: boolean;
  onFormCancel: () => void;
}

export const CommissionsBatchPaymentOrderForm = ({ batch, open, onFormCancel }: Props) => {
  const { t } = useTranslation();

  const [form] = Form.useForm<GenerateBatchPaymentOrder>();
  const gainerIds = Form.useWatch("gainerIds", form);

  const { data, isFetching } = useCalculateCommissionsBatchPayments(
    {
      id: batch.id,
      object: { gainerIds: gainerIds ?? [] },
    },
    open,
  );

  const { data: missingAgents, isFetching: missingAgentsIsFetching } = useGetAgentEnumerations(
    data?.missingBankAccountAgentIds ?? [],
  );

  const generateCommissionsBatchPaymentOrder = useGenerateCommissionsBatchPaymentOrder();

  const disableInputs =
    batch.step !== CommissionsBatchStep.RECAPITULATION &&
    batch.step !== CommissionsBatchStep.REVIEW &&
    batch.step !== CommissionsBatchStep.FINISH;

  const handleFormSubmit = (type: BatchPaymentType) => {
    form
      .validateFields()
      .then((values) =>
        generateCommissionsBatchPaymentOrder.mutate(
          {
            id: batch.id,
            object: { ...values, gainerIds: values.gainerIds, type },
          },
          {
            onSuccess: onFormCancel,
            onError: (error) => setErrorsToForm(form, "commissions.batch.attrs.paymentOrder", error.violations),
          },
        ),
      )
      .catch(resolveFormValidationError);
  };

  return (
    <Modal
      width={ModalSizes.LARGE}
      open={open}
      title={t("commissions.batch.titles.generatePaymentOrder")}
      okText={t("common.download")}
      okButtonProps={{ disabled: disableInputs }}
      cancelText={t("common.cancel")}
      afterClose={() => form.resetFields()}
      onCancel={onFormCancel}
      footer={
        <Space align="end">
          <Button onClick={onFormCancel}>{t("common.cancel")}</Button>

          {isDefined(data?.allGainersPaidAmountSum) ? (
            <Button
              type="primary"
              onClick={() => handleFormSubmit(BatchPaymentType.ALL_GAINERS)}
              disabled={!!data?.missingBankAccountAgentIds.length || disableInputs}
              loading={generateCommissionsBatchPaymentOrder.isPending}
            >
              {t("common.download")}
            </Button>
          ) : (
            <>
              <Button
                type="primary"
                onClick={() => handleFormSubmit(BatchPaymentType.BROKERS)}
                disabled={!!data?.missingBankAccountAgentIds.length || disableInputs}
                loading={generateCommissionsBatchPaymentOrder.isPending}
              >
                {t("common.download")} - {t("commissions.batch.helpers.broker")}
              </Button>
              <Button
                type="primary"
                onClick={() => handleFormSubmit(BatchPaymentType.TIPPERS)}
                disabled={!!data?.missingBankAccountAgentIds.length || disableInputs}
                loading={generateCommissionsBatchPaymentOrder.isPending}
              >
                {t("common.download")} - {t("commissions.batch.helpers.tipper")}
              </Button>
            </>
          )}
        </Space>
      }
      mask={{ closable: false }}
    >
      <Form form={form} layout="vertical" name="commissionsBatchPaymentOrderForm">
        <Row gutter={rowGutter}>
          <Col span={8}>
            <Form.Item
              name="paymentExecutionDate"
              label={t("commissions.batch.attrs.paymentOrder.paymentExecutionDate")}
              rules={[validations.notNull, validations.notPast]}
              {...datePickerFormItemProps}
            >
              <DatePicker {...datePickerStandardProps} minDate={dayjs()} />
            </Form.Item>
          </Col>

          <Col span={8}>
            <Form.Item
              name="markAsPaid"
              className="form-item-without-label"
              valuePropName="checked"
              rules={[validations.none]}
              initialValue={false}
            >
              <Checkbox>{t("commissions.batch.attrs.paymentOrder.markAsPaid")}</Checkbox>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={rowGutter}>
          <Col span={24}>
            <Form.Item
              name="gainerIds"
              label={
                <LabelWithTooltip
                  label={t("commissions.batch.attrs.paymentOrder.gainerIds")}
                  tooltip={t("commissions.batch.helpers.gainerIdsHint")}
                />
              }
              rules={[validations.none]}
            >
              <AgentSelect allowClear isMultiple />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={rowGutter}>
          <Col span={24}>
            <Spin spinning={isFetching}>
              {isDefined(data?.allGainersPaidAmountSum) && (
                <Alert
                  type="info"
                  title={
                    <Space>
                      <span>{t("commissions.batch.helpers.paymentToBePaid")}:</span>
                      <b>{formatLocaleCurrency(data.allGainersPaidAmountSum)}</b>
                    </Space>
                  }
                />
              )}

              {isDefined(data?.brokersPaidAmountSum ?? data?.tippersPaidAmountSum) && (
                <Alert
                  type="info"
                  title={
                    <Descriptions
                      size="small"
                      style={{ width: 340 }}
                      styles={{
                        label: { color: cssVariables.colorText },
                        content: {
                          textAlign: "right",
                          fontWeight: "bold",
                          whiteSpace: "nowrap",
                          justifyContent: "flex-end",
                        },
                      }}
                      column={1}
                      items={[
                        {
                          label: `${t("commissions.batch.helpers.paymentToBePaid")} ${t("commissions.batch.helpers.broker")}`,
                          children: isDefined(data?.brokersPaidAmountSum)
                            ? formatLocaleCurrency(data?.brokersPaidAmountSum)
                            : undefined,
                        },
                        {
                          label: `${t("commissions.batch.helpers.paymentToBePaid")} ${t("commissions.batch.helpers.tipper")}`,
                          children: isDefined(data?.tippersPaidAmountSum)
                            ? formatLocaleCurrency(data?.tippersPaidAmountSum)
                            : undefined,
                        },
                      ]}
                    />
                  }
                />
              )}

              {data?.missingBankAccountAgentIds.length ? (
                <Alert
                  className="margin-top-small margin-bottom-small"
                  type="error"
                  title={
                    <LabelWithPopover
                      popoverContent={
                        <Spin spinning={missingAgentsIsFetching}>
                          <div style={{ height: 200, overflow: "auto", minWidth: 200 }}>
                            <List
                              items={missingAgents ?? []}
                              renderItem={(item) => (
                                <Link
                                  to={generatePath(AGENT_ROUTE_PATHS.detail.to, { id: item.id })}
                                  target="_blank"
                                  style={{
                                    borderBottom: `1px solid ${cssVariables.colorHover}`,
                                    paddingBottom: 4,
                                    marginRight: 16,
                                  }}
                                >
                                  {formatAgentAggregatedName(item)}
                                </Link>
                              )}
                              gap={8}
                            />
                          </div>
                        </Spin>
                      }
                      popoverTitle={t("commissions.batch.titles.missingBankAccountAgents")}
                      label={t("commissions.batch.helpers.missingBankAccountAgentLabel", {
                        count: data.missingBankAccountAgentIds.length,
                      })}
                    />
                  }
                />
              ) : undefined}
            </Spin>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};
